import { Trans } from '@lingui/macro'
import {
  Alert, AlertTitle,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Icon,
  IconButton,
  Typography
} from '@mui/material'
import useMouse from '@react-hook/mouse-position'
import { Font, PDFDownloadLink, PDFViewer } from '@react-pdf/renderer'
import { useQuery } from '@tanstack/react-query'
import { defaultDocTitle } from 'app/appSettings'
import openSansBold from 'app/assets/fonts/OpenSans-Bold.ttf'
import openSansBoldItalic from 'app/assets/fonts/OpenSans-BoldItalic.ttf'
import openSansItalic from 'app/assets/fonts/OpenSans-Italic.ttf'
import openSansRegular from 'app/assets/fonts/OpenSans-Regular.ttf'
import PigiarniqBold from 'app/assets/fonts/Pigiarniq-Bold.ttf'
import PigiarniqItalic from 'app/assets/fonts/Pigiarniq-Italic.ttf'
import PigiarniqRegular from 'app/assets/fonts/Pigiarniq.ttf'
import robotoBold from 'app/assets/fonts/Roboto-Bold.ttf'
import robotoBoldItalic from 'app/assets/fonts/Roboto-BoldItalic.ttf'
import robotoItalic from 'app/assets/fonts/Roboto-Italic.ttf'
import robotoRegular from 'app/assets/fonts/Roboto-Regular.ttf'
import { authRoles, checkAuth, hasRole } from 'app/auth/authRoles'
import { queryCreateObject } from 'app/backend/Common'
import { useGlobalQueries } from 'app/GlobalQueriesProvider'
import { useFormLanguageRestriction } from 'app/hooks/useFormLanguageRestriction'
import { usePortalTranslations } from 'app/hooks/usePortalTranslations'
import SFAuthService from 'app/services/sfAuth/SFAuthService'
import {
  createOpportunityByFlow
} from 'app/services/sfAuth/sfData/sfOpportunity'
import Loading from 'egret/components/EgretLoadable/Loading'
import { Formik } from 'formik'
import _, { isError } from 'lodash'
import isEqual from 'lodash.isequal'
import { useSnackbar } from 'notistack'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useHistory } from 'react-router-dom'
import { isValidSalesforceId } from 'utilsTS'
import * as Yup from 'yup'
import sfOauthConfig from '../../services/sfAuth/sfAuthConfig'
import { DialogTitleWithIconClose } from '../common-components/DialogTitleWithIconClose'
import { AlertBanner } from '../common/alerts/AlertBanner'
import { parseExtensionPhoneToSF } from '../common/Formats'
import { GRANTS_PATH } from '../grants/GrantsConstants'
import PopUpDialogue from '../grants/PopUpDialogue'
import { RESOURCE_INVENTORY_PATHS } from '../resources/ResourcesConstants'
import { NOT_FOUND_PATH } from '../sessions/SessionRoutes'
import {
  getElementIdToFormPosition,
  parseFormLabelText
} from './common/Common'
import './common/FormTypography.scss'
import { formComponentTypes } from './components/formComponentTypes'
import { formObjectsToConnect } from './editor/FormWizard'
import { CollapseElement } from './form-page/CollapseElement'
import { FormContextProvider } from './form-page/FormContext'
import { FormDisplayContainer } from './form-page/FormDisplayContainer'
import FormElementGroup from './form-page/FormElementGroup'
import { StepperButtons } from './form-page/StepperButtons'
import './Form.scss'
import { FormErrorPopUp } from './FormErrorPopUp'
import {
  getDisabledIds,
  getValidCurrentStepIndex,
  isConditionMet,
  sectionConditionId
} from './formHelpersConditions'
import { fetchData as pureFetchData } from './formHelpersFetchData'
import {
  constructValidationSchema,
  correctableErrors,
  errorsToRender,
  getInitialTouched
} from './formHelpersValidation'
import { FormMissingObjectsObserver } from './FormMissingObjectsObserver'
import FormSavingHandler from './FormSavingHandler'
import { connectedObjectQuery } from './formsHelpersQueries'
import { FormStepper } from './FormStepper'
import { checkIfSavingResultHasErrors } from './formUtils'
import CursorIcon from './multiuser/components/CursorIcon'
import FormMultiuser from './multiuser/FormMultiuser'
import {
  formRealmId
} from './multiuser/grpcMultiuserEdit'
import FormPdfDocument from './pdf-components/FormPdf'
import defaultHyphenCb from './pdf-components/hyphenation'

export const formItemPadding = 12
export const DOCUMENT_TYPE_GRANTEE_REPORT = 'Grantee report'
export const DEFAULT_FORM_SAVE_REJECT = 'DEFAULT_FORM_SAVE_REJECT'

export const baseLogoSize = 100

Font.register({
  family: 'Roboto',
  fonts: [
    { src: robotoBoldItalic, fontWeight: 700, fontStyle: 'italic' },
    { src: robotoRegular }, // font-style: normal, font-weight: normal
    { src: robotoBold, fontWeight: 700 },
    { src: robotoItalic, fontStyle: 'italic' }
  ]
})
Font.register({
  family: 'Roboto serif',
  fonts: [
    { src: robotoBoldItalic, fontWeight: 700, fontStyle: 'italic' },
    { src: robotoRegular }, // font-style: normal, font-weight: normal
    { src: robotoBold, fontWeight: 700 },
    { src: robotoItalic, fontStyle: 'italic' }
  ]
})
Font.register({
  family: 'Open Sans',
  fonts: [
    { src: openSansBoldItalic, fontWeight: 700, fontStyle: 'italic' },
    { src: openSansRegular }, // font-style: normal, font-weight: normal
    { src: openSansBold, fontWeight: 700 },
    { src: openSansItalic, fontStyle: 'italic' }
  ]
})
Font.register({
  family: 'Pigiarniq',
  fonts: [
    { src: PigiarniqRegular }, // font-style: normal, font-weight: normal
    { src: PigiarniqBold, fontWeight: 700 },
    { src: PigiarniqItalic, fontStyle: 'italic' },
    { src: PigiarniqBold, fontWeight: 700, fontStyle: 'italic' }
  ]
})

Font.registerHyphenationCallback((word, glyphString) => {
  if (Number.isInteger(parseInt(word))) {
    return [word]
  }
  return defaultHyphenCb(word, glyphString)
})

export const insertValueToSFObject = ({
  saveMap,
  value,
  fieldProps,
  sfObject,
  subObjectsMap,
  connectedObjectId,
  customConnectedField = false
}) => {
  const { name, subObject } = fieldProps
  if (subObject && name.indexOf('.') !== '-1') {
    const subObjectName = name.split('.')[0]
    const subFieldName = name.split('.')[1]
    if (subObjectName && subFieldName) {
      if (customConnectedField) {
        sfObject = sfObject[subObjectName]
      }
      subObjectsMap[sfObject.Id] = { type: subObject }
      if (!saveMap[sfObject.Id]) {
        saveMap[sfObject.Id] = {
          Id: sfObject.Id
        }
      } else {
        saveMap[sfObject.Id][subFieldName] = value
      }
    }
  } else {
    saveMap[connectedObjectId][name] = value
  }
}

export const getSFObjectFieldValue = (sfObject, props) => {
  if (!props) {
    return null
  }
  let sfValue = _.get(sfObject, props.name)
  if (props.subObject && sfValue) {
    const mainField = props.name.split('.')[0]
    const subField = props.name.split('.')[1]
    sfValue = sfObject[mainField][subField]
  }
  return sfValue
}

export const getMainConnected = (item) => {
  const connected = item.typeProps.connectedTo || []
  let toRet = {}
  connected.some((obj, index) => {
    const noField = formComponentTypes[item.elementType]?.noFieldConnect
    if (obj.connectedObject && (obj.connectedField || noField)) {
      toRet = obj
      return true
    }
    if (index === 0) {
      // console.error('Main connected object was empty!', item)
    } else {
      console.warn('Empty connection configured detected for:', item)
    }
    return false
  })
  return toRet
}

export const getConnectedObjectOfType = (item, type) => {
  const connected = item.typeProps.connectedTo || []
  let toRet = {}
  connected.some((obj, index) => {
    if (obj.connectedObject && (obj.forceType === type || obj.type === type)) {
      toRet = obj
      return true
    }
    return false
  })
  return toRet
}

export const mapFormElements = (data, langVersion, mapGroups = false) => {
  const returnObj = {}
  if (!data || !data?.sections) {
    return returnObj
  }
  const mapItem = ({
    item,
    returnObj,
    section,
    langVersion,
    sectionIndex,
    mapGroups
  }) => {
    if (item.elements) {
      item.elements.forEach((element) =>
        mapItem({
          item: element,
          returnObj,
          section,
          langVersion,
          sectionIndex,
          mapGroups
        })
      )
      if (mapGroups) {
        returnObj[item.id] = {
          ...item,
          title: parseFormLabelText({
            text: item.title,
            langVersion
          }),
          sectionIndex,
          sectionName: parseFormLabelText({
            text: section.title,
            langVersion
          })
        }
      }
    } else {
      returnObj[item.id] = {
        ...item,
        title: parseFormLabelText({
          text: item.title,
          langVersion
        }),
        sectionIndex,
        sectionName: section.title?.[langVersion]
      }
    }
  }

  data?.sections.forEach((section, sectionIndex) =>
    section.elements.forEach((item) =>
      mapItem({
        item,
        returnObj,
        section,
        langVersion,
        sectionIndex,
        mapGroups
      })
    )
  )
  return returnObj
}

export const handleFormSave = ({
  values,
  appConfigurations,
  reduxBag,
  utilityBag = {},
  elementsMap,
  connectedMap,
  disabledIds = [],
  baseToSave = {},
  network,
  mainObject,
  isSubmitting,
  saveErrorCallback,
  budgetCategories
}) => {
  const conn = SFAuthService.getConnection()
  const { enqueueSnackbar, history } = utilityBag
  let promises = []
  const afterMainSaveQueries = []
  const subObjectsMap = {}
  if (!connectedMap || Object.keys(connectedMap).length === 0) {
    return Promise.resolve()
  }
  console.log('form save values', values, connectedMap, baseToSave)
  const saveMap = { ...baseToSave }
  Object.keys(connectedMap).forEach((key) => {
    if (connectedMap[key].sfObject) {
      saveMap[key] = {
        ...saveMap[key],
        Id: saveMap[key]?.Id || connectedMap[key].sfObject.Id
      }
    }
  })
  Object.keys(values)
    .filter((key) => {
      const item = elementsMap[key]
      return !disabledIds.includes(key) || item?.forceSaveWhenDisabled
    })
    .forEach((key, index) => {
      const item = elementsMap[key]
      if (item) {
        const elementProps = formComponentTypes[item.elementType]
        const typeProps = item.typeProps
        const {
          connectsToMultipleObjects,
          savePromise,
          savesOnlyOnce
        } = elementProps
        const {
          isConnected,
          connectedTo = [],
          options,
          requiresRequest,
          isPhone,
          readOnly
        } = typeProps
        let sfObject, additionalSFInfo
        if (!readOnly) {
          let value = values[key]
          // Handle saving for form elements that should just save once, but have multiple objects connected
          if (savesOnlyOnce && savePromise) {
            const { promise, afterMainPromise } = savePromise({
              value,
              item,
              connectedMap,
              appConfigurations,
              network
            })

            if (afterMainPromise) {
              afterMainSaveQueries.push(promise)
            } else {
              promises.push(promise)
            }
          } else if (connectsToMultipleObjects && savePromise) {
            const { promise, afterMainPromise } = savePromise({
              value,
              item,
              connectedObject: sfObject,
              additionalSFInfo,
              appConfigurations
            })
            if (afterMainPromise) {
              afterMainSaveQueries.push(promise)
            } else {
              promises.push(promise)
            }
          } else {
            // Handle saving for regular form elements which should save value to every object connecteds

            // This is a flag for phone numbers to check if the value has been parsed.
            // It is used to avoid parsing the same value multiple times when there are multiple connected objects.
            let isPhoneValueParsed = false
            connectedTo.forEach((obj, index) => {
              let { connectedObject, connectedField } = obj
              if (
                isConnected &&
                connectedObject &&
                connectedMap[connectedObject]
              ) {
                sfObject = connectedMap[connectedObject].sfObject
                additionalSFInfo = connectedMap[connectedObject].additionalInfo
                let fieldName = connectedField?.name
                if (connectedField && fieldName.indexOf('.') !== '-1') {
                  const subObjectName = fieldName.split('.')[0]
                  const subFieldName = fieldName.split('.')[1]
                  if (subObjectName && subFieldName) {
                    fieldName = subFieldName
                    sfObject = sfObject[subObjectName]
                    connectedObject = sfObject.Id
                    subObjectsMap[sfObject.Id] = {
                      type: connectedField.subObject
                    }
                    if (!saveMap[sfObject.Id]) {
                      saveMap[sfObject.Id] = {
                        Id: sfObject.Id
                      }
                    }
                  }
                }
                if (options) {
                  options.forEach((option, index) => {
                    let isSelected = false
                    const defaultOpt = 'option' + index
                    const optionValue = isConnected
                      ? option.apiValue || defaultOpt
                      : defaultOpt
                    if (Array.isArray(value)) {
                      isSelected = value.includes(optionValue)
                    } else {
                      isSelected = value === optionValue
                    }
                    if (isSelected && option.requireDetails) {
                      if (option.connectedField) {
                        let fieldValue = values.other && values.other[key]
                        if (Array.isArray(fieldValue)) {
                          fieldValue = fieldValue[index]
                        }
                        saveMap[connectedObject][option.connectedField.name] =
                          fieldValue
                      }
                    }
                  })
                }
                if (typeProps.picklistType === 'multiselect' && value) {
                  value = value.join(';')
                }
                // If empty string is sent to SF instead of null  the field will be set to 0 instead of null
                if (
                  item.elementType === 'textInputNumeric' ||
                  item.elementType === 'numericSlider'
                ) {
                  if (!value && value !== 0) {
                    value = null
                  } else if (isPhone && !isPhoneValueParsed) {
                    value = parseExtensionPhoneToSF(value, values.other[key])
                    isPhoneValueParsed = true
                  }
                }
                let saveHandled = false
                if (savePromise) {
                  saveHandled = true
                  const { promise, afterMainPromise } = savePromise({
                    value,
                    item,
                    connectedMap,
                    connectedObject: sfObject,
                    additionalSFInfo,
                    appConfigurations,
                    network
                  })
                  if (afterMainPromise) {
                    afterMainSaveQueries.push(promise)
                  } else {
                    promises.push(promise)
                  }
                }
                if (elementProps.extractSaveKey) {
                  saveHandled = true
                  elementProps.extractSaveKey({
                    saveMap,
                    subObjectsMap,
                    additionalSFInfo,
                    value,
                    values,
                    item,
                    connectedProps: obj,
                    connectedObjectId: connectedObject,
                    connectedTo,
                    sfObject,
                    appConfigurations,
                    budgetCategories
                  })
                }
                if (!saveHandled && !requiresRequest && fieldName) {
                  saveMap[connectedObject][fieldName] = value
                }
              }
            })
          }
        }
      }
    })

  Object.keys(saveMap).forEach((key) => {
    const objData = connectedMap[key]?.sfObject
    const type = objData ? objData.attributes.type : subObjectsMap[key].type
    const sfObjectData = formObjectsToConnect[type]
    if (saveMap[key].Id && Object.keys(saveMap[key]).length > 1) {
      if (sfObjectData && sfObjectData.saveFunction) {
        promises.push(
          sfObjectData.saveFunction(
            saveMap[key],
            reduxBag,
            utilityBag
          )
        )
      } else {
        promises.push(conn.sobject(type).update(saveMap[key]))
      }
    } else if (!saveMap[key].Id) {
      if (type === 'Opportunity') {
        promises.push(createOpportunityByFlow(conn, saveMap[key]).then(result => {
          return {
            ...result,
            objectType: type
          }
        }))
      } else {
        delete saveMap[key].Id
        if (Object.keys(saveMap[key]).length > 0) {
          if (sfObjectData && sfObjectData.createFunction) {
            promises.push(
              sfObjectData.createFunction(
                saveMap[key],
                reduxBag,
                utilityBag
              )
            )
          } else {
            promises.push(conn.sobject(type).create(saveMap[key]).then(result => {
              return {
                ...result,
                objectType: type
              }
            }))
          }
        }
      }
    }
  })

  promises = promises.filter((f) => f)

  const handleReject = (reject) => {
    let correctableError, correctableErrorHandled

    const showCorrectableError = (text) => {
      if (!correctableErrorHandled) {
        correctableErrorHandled = true
        enqueueSnackbar(text, {
          variant: 'error'
        })
      }
    }

    let stringifiedError
    const getErrorFromSavingResult = (error, handled = false) => {
      if (Array.isArray(error)) {
        return error.some((error) => getErrorFromSavingResult(error))
      } else if (error.compositeResponse) {
        return error.compositeResponse.some((result) => {
          if (result.body) {
            return result.body.some((result) => getErrorFromSavingResult(result))
          }
          return false
        })
      } else if (error.errors) {
        return error.errors.some((error) => {
          if (error.statusCode) {
            stringifiedError = error.statusCode
            return true
          }
          return false
        }
        )
      } else {
        let parsedError = error
        if (isError(error)) {
          parsedError = reject.toString()
        } else if (
          (error.errorCode ||
            error.statusCode === 'FIELD_CUSTOM_VALIDATION_EXCEPTION') &&
          error.message
        ) {
          parsedError = error.message
        }
        if (error.outputValues && error.outputValues.error) {
          parsedError = error.outputValues.error
        }
        if (typeof parsedError === 'string') {
          Object.keys(correctableErrors).forEach((key) => {
            const errorData = correctableErrors[key]
            if (errorData.logic(parsedError)) {
              correctableError = key
              if (errorData.text) {
                showCorrectableError(errorData.text)
              }
            }
          })
          stringifiedError = parsedError
          return true
        } else {
          return false
        }
      }
    }

    getErrorFromSavingResult(reject)

    return Promise.reject({
      reject: correctableError || DEFAULT_FORM_SAVE_REJECT,
      showSnackbar: !correctableError,
      savingResult: reject,
      extractedError: stringifiedError
    })
  }

  return Promise.allSettled(promises).then(
    (result) => {
      const handleFinalResult = (combinedResult) => {
        let errorOcurred
        console.log('form saving result', combinedResult)
        combinedResult.forEach((promise) => {
          if (promise.status === 'rejected') {
            errorOcurred = true
          } else if (!errorOcurred) {
            errorOcurred =
              Array.isArray(promise.value) &&
              promise.value.some((res) => checkIfSavingResultHasErrors(res))
          }
        })
        if (errorOcurred) {
          const rejectArray = combinedResult.map((promise) =>
            promise.status === 'rejected' ? promise.reason : promise.value
          )
          return handleReject(rejectArray)
        } else {
          if (!isSubmitting) {
            if (mainObject === 'Resource__c') {
              const createResourceResult = combinedResult[0].value
              const resourceId = createResourceResult?.id
              if (resourceId) {
                history.push(RESOURCE_INVENTORY_PATHS.resourceInventorySubmitResource + '/' + resourceId)
              }
            }
          }
          return combinedResult.map((promise) =>
            promise.status === 'rejected' ? promise.reason : promise.value
          )
        }
      }
      if (afterMainSaveQueries.length > 0) {
        const postPromises = []
        afterMainSaveQueries.forEach((callback) => {
          if (Array.isArray(callback)) {
            return callback.forEach((chidCallback) => {
              postPromises.push(chidCallback())
            })
          } else {
            return callback()
          }
        })
        return Promise.allSettled(postPromises).then((postResult) => {
          return handleFinalResult([...result, ...postResult])
        })
      } else {
        return handleFinalResult(result)
      }
    },
    (reject) => {
      console.error('form error saving', reject)
      return handleReject(reject)
    }
  )
}

export const getObjectsFieldsMap = ({
  data,
  connectedMap = {},
  describeMap
}) => {
  const objectsFieldsMap = {}
  const missingObjects = []

  if (!data?.objectsConnected) {
    return {
      objectsFieldsMap,
      missingObjects
    }
  }

  data.objectsConnected.forEach((object) => {
    const connData = connectedMap[object.identId]
    if (!connData) {
      return
    }

    if (
      connectedMap.undefined &&
      typeof connectedMap.undefined === 'object'
    ) {
      missingObjects.push({
        type: connectedMap.undefined.objectType
      })
    }
    const toSet = {}
    const objName = connData.sfObject.attributes.type

    Object.keys(connData.fieldsMap).forEach((key) => {
      toSet[key] = {
        value: connData.sfObject[key],
        type: connData.fieldsMap[key].type
      }
    })

    Object.keys(connData.sfObject).forEach((key) => {
      if (!toSet[key]) {
        toSet[key] = {
          value: connData.sfObject[key],
          type: 'object'
        }
      }
    })

    if (!connData.sfObject?.Id?.value && !connData.sfObject?.Id) {
      missingObjects.push({
        type: object.type
      })
    }

    const config = formObjectsToConnect?.[objName]

    if (config?.additionalObjects) {
      config.additionalObjects.forEach((obj) => {
        const { field } = obj
        const subObject = connData.sfObject[field]

        if (subObject) {
          const fields = describeMap?.[subObject.attributes?.type]?.fields
          const subObjectFieldsMap = {}

          if (fields) {
            fields.forEach((f) => {
              subObjectFieldsMap[f.name] = {
                value: subObject[f.name],
                type: f.type
              }
            })
          }

          toSet[field] = {
            value: subObjectFieldsMap,
            type: 'object'
          }
        }
      })
    }

    objectsFieldsMap[object.name] = toSet
  })

  return {
    objectsFieldsMap,
    missingObjects
  }
}

const Form = ({ defaultFormType = 'editable', ...props }) => {
  const editingUsers = useSelector((state) => state.multiuser.editingUsers)
  const user = useSelector((state) => state.user)
  const multiuserEdit = editingUsers && Object.values(editingUsers).length > 0
  const mouseDetectRef = useRef()
  const [currentStep, setStep] = useState(0)
  const [wrongAccountRole, setWrongAccountRole] = useState(null)

  const mousePosition = useMouse(mouseDetectRef, {
    enterDelay: 100,
    leaveDelay: 100,
    fps: 1
  })

  if (wrongAccountRole === 'prevent') {
    return (
      <AlertBanner
        title={<Trans>FORMS_NO_VIEW_OR_EDIT_BANNER_TITLE</Trans>}
        description={<Trans>FORMS_REQUEST_ACCESS_BANNER_TEXT</Trans>}
        // description={<Trans>FORMS_YOU_CAN_REQUEST_ACCESS <MuiLink component={RouterLink} to="/grants/Organizations">HERE</MuiLink></Trans>}
      />
    )
  }

  return (
    <>
      {wrongAccountRole === 'warning' && <AlertBanner
        title={<Trans>FORMS_NO_EDIT_BANNER_TITLE</Trans>}
        description={<Trans>FORMS_REQUEST_ACCESS_BANNER_TEXT</Trans>}
                                         />}
      {multiuserEdit && (
        <div style={{ height: 1 }}>
          {Object.values(editingUsers)
            .filter(
              (userObj) =>
                userObj.id !== user.userId && userObj.step === currentStep
            )
            .map((userObj, index) => {
              const { coordinates, color, name } = userObj
              return (
                coordinates &&
                mousePosition.elementHeight && (
                  <CursorIcon
                    coordinates={coordinates}
                    dimensions={
                      {
                        width: mousePosition.elementWidth,
                        height: mousePosition.elementHeight
                      }
                    }
                    index={index}
                    color={color}
                    name={name}
                  />
                )
              )
            })}
        </div>
      )}
      <FormWrapped
        {...props}
        defaultFormType={defaultFormType}
        mouseDetectRef={mouseDetectRef}
        currentStep={currentStep}
        setStep={setStep}
        mousePosition={mousePosition}
        wrongAccountRole={wrongAccountRole}
        setWrongAccountRole={setWrongAccountRole}
      />
    </>
  )
}

const FormWrapped = React.memo(
  ({
    match = {},
    customTitleRender,
    forceFormType,
    saveCallback,
    fetchString,
    formId,
    scrollbarContentRef,
    disableTitle = false,
    pdfTitle,
    returnPdf,
    displayView,
    forceDisabled,
    disablePDF,
    inDialog,
    onDialogClose,
    mouseDetectRef,
    currentStep,
    setStep,
    renderData,
    defaultFormType,
    selectedFormType,
    noDefaultSave,
    style,
    containerViewType = 'none',
    hideLoading,
    mainObject,
    infoBanner,
    banner,
    blockPDFDownload,
    mousePosition,
    shouldSavePdfOnSubmit, // if true, will save pdf in the Opportunity object on submit
    loadingComponent, // component to show when loading
    children,
    submitCallback,
    wrongAccountRole,
    setWrongAccountRole,
    onPdfCreated, // callback when pdf is created. It will return the @react-pdf/renderer document
    disableAutoTabTitle, // when true, prevents automatic syncing of the form name to the browser tab title
    focusFirstError, // automatically scrolls to and focuses the first invalid field
    keepLanguageRestriction = false, // when true, the language is not reset to its pre-form value on unmount
    ...props
  }) => {
    const { params = {} } = match
    const isPreview = props.preview
    formId = formId || params.formId
    const [id, setId] = useState(fetchString || params.id)
    const [disabled, setDisabled] = useState(false)
    const [loading, setLoading] = useState(!renderData)
    const [initialValues, setInitialValues] = useState()
    const [data, setData] = useState()
    const [validationSchema, setValidationSchema] = useState()
    const [validationSchemaRefs, setValidationSchemaRefs] = useState({})
    const [connectedMap, setConnectedMap] = useState({})
    const [describeMap, setDescribeMap] = useState({})
    const [configuration, setConfiguration] = useState()
    const [stage, setStage] = useState()
    const [readOnly, setReadOnly] = useState(false)
    const [pdfDisplay, setPdfDisplay] = useState(false)
    const [formViewType, setFormViewType] = useState('editable')
    const [supportedFormViews, setSupportedFormViews] = useState([])
    const [useMultiuser, setUseMultiuser] = useState(false)
    const [multiuserSessionToken, setMultiuserSessionToken] = useState(null)
    const [notSupportedError, setNotSupportedError] = useState(false)
    const [initialValuesError, setInitialValuesError] = useState(null)
    const [smallSpace, setSmallSpace] = useState({
      stepper: false,
      buttons: false
    })
    const user = useSelector((state) => state.user)
    const organization = useSelector((state) => state.organization || {})
    const { getTranslation } = usePortalTranslations()
    const collapsable = props.collapsable || 'none'
    const overrideFormLanguage = data?.overrideFormLanguage
    const { language } = user
    const langVersion_ = overrideFormLanguage || language?.split('_')[0] || 'en'
    const langVersion = overrideFormLanguage || (data?.translatedFor?.[langVersion_]?.value
      ? langVersion_
      : 'en')
    const organizationId = organization.id || params.organizationId
    const { enqueueSnackbar } = useSnackbar()
    const history = useHistory()
    const formikRef = useRef()
    const prevValidationInfoRef = useRef()
    const noConnectedObjects =
      data && data.objectsConnected.length === 0
    /** Ref for the Stepper element. This is used to calculate width of panels with avatars in Stepper */
    const { sections = [], title } = data || {}

    const showPrintButton = data?.showPrintButton
    const hideFormTitle = data?.hideFormTitle
    const doNotUseStickyHeader = data?.doNotUseStickyHeader

    const { network, fundingStreams, budgetCategories } = useGlobalQueries()

    const avaliableInLanguages = useMemo(() => {
      if (!fundingStreams?.length) return undefined
      console.log({ connectedMap })
      for (const obj of Object.values(connectedMap)) {
        if (obj?.objectType !== 'Funding_Stream__c') continue
        const fs = fundingStreams.find(fs => fs.id === obj?.sfObject?.Id)
        if (fs?.avaliableInLanguages?.length) return fs.avaliableInLanguages
      }
      return undefined
    }, [connectedMap, fundingStreams])

    const { showLangWarning, switchToSupportedLanguage } = useFormLanguageRestriction(avaliableInLanguages, false, keepLanguageRestriction)

    const ownTitleRef = useRef()
    const hasNavigatedToFirstError = useRef(false)

    const { objectsFieldsMap, missingObjects } = getObjectsFieldsMap({
      data,
      connectedMap,
      describeMap
    })

    let formTitlePdf = parseFormLabelText({
      text: title?.[langVersion].text,
      langVersion,
      objectsFieldsMap,
      describeMap,
      returnString: true
    })
    const cleanedTitle = formTitlePdf.trim()
    if (cleanedTitle === ':' && !!title?.[langVersion]?.text) {
      formTitlePdf = title[langVersion].text
    }

    const elementsMap = mapFormElements(data, langVersion)

    const handleInitialValuesError = (error) => {
      setInitialValuesError(error)
      setLoading(false)
    }

    const scrollToTop = () => {
      if (scrollbarContentRef && scrollbarContentRef.current) {
        setTimeout(() => {
          scrollbarContentRef.current.scrollToTop()
          scrollbarContentRef.current.handleWindowResize()
        }, 500)
      }
    }

    const navigateToError = ({ section, id }) => {
      setStep(section)
      if (scrollbarContentRef && scrollbarContentRef.current) {
        setTimeout(() => {
          const domElement = document.getElementById(id)
          scrollbarContentRef.current.scrollTop(domElement.offsetTop)
        }, 300)
      } else {
        setTimeout(() => {
          const domElement = document.getElementById(id)
          domElement.scrollIntoView({ behavior: 'instant', block: 'start' })
        }, 300)
      }
    }

    const handleObjectMissing = () => {
      handleInitialValuesError({
        message: 'INSUFFICIENT_ACCESS',
        type: 'Loading error'
      })
    }

    const checkIfFormValidationShouldRebuild = ({ sections, values }) => {
      let shouldRebuild = false
      const requiredFromConditions = []
      const nonRequiredFromConditions = []
      const validationInfoFromConditions = {}
      const elementIdToFormPosition = getElementIdToFormPosition(sections)

      const refsCopy = { ...validationSchemaRefs }
      const errors =
        formikRef && formikRef.current ? formikRef.current.errors : {}
      const checkRequiredConditions = (item) => {
        if (item.elements) {
          item.elements.forEach((element) => {
            checkRequiredConditions(element)
          })
        }
        if (item.conditions) {
          const activeConditions = []
          item.conditions
            .filter((condition) =>
              [
                'required',
                'notRequired',
                'minFiles',
                'maxMin',
                'numMin',
                'hide',
                'show'
              ].includes(condition.state)
            )
            .forEach((condition) => {
              const { conditionMet, state } = isConditionMet({
                condition,
                elementsMap,
                values,
                langVersion,
                connectedMap,
                errors,
                pathname
              })

              if (conditionMet) {
                activeConditions.push({ condition, state })
              }
              const requiredCondition = activeConditions.find(c => c.state === 'required')
              const notRequiredCondition = activeConditions.find(c => c.state === 'notRequired')
              const hideCondition = activeConditions.find(c => c.state === 'hide')
              const showCondition = activeConditions.find(c => c.state === 'show')
              const hasRequiredConflict = requiredCondition && notRequiredCondition
              const hasVisibilityConflict = hideCondition && showCondition

              const numericConditions = {
                maxMin: activeConditions.filter(c => c.state === 'maxMin'),
                numMin: activeConditions.filter(c => c.state === 'numMin'),
                minFiles: activeConditions.filter(c => c.state === 'minFiles')
              }

              // Check for numeric conflicts
              const numericConflicts = Object.entries(numericConditions)
                .filter(([_, conditions]) => conditions.length > 1)
                .map(([type, conditions]) => ({
                  type,
                  values: conditions.map(c => c.condition[type])
                }))

              const showRequiredError = hasRequiredConflict && ['required', 'notRequired'].includes(state)
              const showVisibilityError = hasVisibilityConflict && ['hide', 'show'].includes(state)
              const showNumericError = numericConflicts.length > 0 && Object.keys(numericConditions).includes(state)

              if (
                showRequiredError ||
                showVisibilityError ||
                showNumericError
              ) {
                const elementIndex = elementIdToFormPosition[item.id]
                if (showRequiredError) {
                  console.error(
                    `Required conflict detected for element ${elementIndex}.${item.title?.en}`
                  )
                }

                if (showVisibilityError) {
                  console.error(
                    `Visibility conflict detected for element ${elementIndex}.${item.title?.en}`
                  )
                }

                numericConflicts.forEach((conflict) => {
                  console.error(
                    `Conflicting ${conflict.type} conditions detected for element ${elementIndex}.${item.title?.en}: 
                    Multiple values [${conflict.values.join(', ')}] are active. Using first value.`
                  )
                })
                return
              }

              const yupField = validationSchema?.fields[item.id]
              const currentRefs = { ...refsCopy[item.id] }

              if (state === 'required' || state === 'notRequired') {
                if (conditionMet) {
                  if (state === 'notRequired') {
                    nonRequiredFromConditions.push(item.id)
                  } else {
                    requiredFromConditions.push(item.id)
                  }
                  if (yupField && state === 'notRequired') {
                    shouldRebuild = true
                  } else if (!yupField && state !== 'notRequired') {
                    shouldRebuild = true
                  }
                } else {
                  if (state === 'required') {
                    nonRequiredFromConditions.push(item.id)
                  }
                  if (
                    yupField &&
                    state === 'required' &&
                    !item.typeProps.required
                  ) {
                    shouldRebuild = true
                  } else if (!yupField && state === 'notRequired') {
                    // shouldRebuild = true
                  } else if (item.typeProps.required && !yupField) {
                    shouldRebuild = true
                  }
                }
              }

              const validationConditions = [
                { key: 'maxMin', typePropsKey: 'max' },
                { key: 'numMin', typePropsKey: 'min' },
                { key: 'minFiles', typePropsKey: 'minFiles' }
              ]
              validationConditions.forEach((obj) => {
                const { key, typePropsKey } = obj

                if (state === key) {
                  if (conditionMet) {
                    validationInfoFromConditions[item.id] = {
                      [typePropsKey]: +condition[key]
                    }
                  }

                  const parameter = +condition[key]

                  if (parameter) {
                    const currentParameter = currentRefs?.[typePropsKey]
                    const paramsMissmatch =
                      currentParameter &&
                      currentParameter !== parameter

                    const paramsAreTheSame =
                      currentParameter &&
                      currentParameter === parameter

                    if (
                      (!currentParameter || paramsMissmatch) &&
                      conditionMet
                    ) {
                      // shouldRebuild = true
                    } else if (paramsAreTheSame && !conditionMet) {
                      shouldRebuild = true
                      delete validationInfoFromConditions[item.id]
                    }
                  }
                }
              })
            })
        }
      }
      sections.forEach((section) => checkRequiredConditions(section))

      if (!isEqual(validationInfoFromConditions, prevValidationInfoRef.current)) {
        shouldRebuild = true
        prevValidationInfoRef.current = { ...validationInfoFromConditions }
      }

      if (shouldRebuild) {
        console.log(
          'validation change from conditions detected. Form will rebuild validation schema',
          validationInfoFromConditions
        )
        setValidationSchemaRefs(validationInfoFromConditions)
        setValidationSchema(
          constructValidationSchema({
            data,
            describeMap,
            requiredFromConditions,
            nonRequiredFromConditions,
            validationInfoFromConditions,
            langVersion: user.language?.split('_')[0] || 'en',
            formikRef
          })
        )
      }
    }

    const fetchData = async ({ inBackground = true, multiuserReload = false, reloadId }) => {
      if (!inBackground) {
        setLoading(true)
      }

      const existingData = renderData || data
      if (!existingData && !isValidSalesforceId(formId)) {
        history.push(NOT_FOUND_PATH)
        return
      }

      const result = await pureFetchData({
        shouldHandleMultiuser: true,
        requireValidationScheme: true,
        multiuserReload,
        reloadId,
        enqueueSnackbar,
        formId,
        formikRef,
        fundingStreams,
        mainObject,
        returnPdf,
        handleObjectMissing,
        id,
        organization,
        isPreview,
        renderData,
        user,
        formType: selectedFormType || forceFormType || defaultFormType,
        params,
        disabled: props.disabled
      })

      const resultKeyToSetter = {
        wrongAccountRole: setWrongAccountRole,
        stage: setStage,
        configuration: setConfiguration,
        disabled: setDisabled,
        validationSchema: setValidationSchema,
        initialValuesError: setInitialValuesError,
        supportedFormViews: setSupportedFormViews,
        notSupportedError: setNotSupportedError,
        pdfDisplay: setPdfDisplay,
        formViewType: setFormViewType,
        readOnly: setReadOnly,
        connectedMap: setConnectedMap,
        describeMap: setDescribeMap,
        data: setData,
        initialValues: setInitialValues,
        multiuser: setUseMultiuser
      }

      if (result.initialValuesError) {
        handleInitialValuesError(result.initialValuesError)
        return
      }

      Object.keys(resultKeyToSetter).forEach((key) => {
        if (result[key]) {
          resultKeyToSetter[key](result[key])
        }
      })

      if (result?.error) {
        enqueueSnackbar(result.error, { variant: 'error' })
      }

      if (result?.preventAccess) {
        history.push(GRANTS_PATH.HOME)
      }

      setLoading(false)
    }

    useEffect(() => {
      if (fetchString !== id && data) {
        setId(fetchString)
        fetchData({
          reloadId: fetchString,
          inBackground: false
        })
      }
    }, [fetchString])

    /* Fetch data if form is loaded and url changes (So if the same form is used to load different object) */
    useEffect(() => {
      if (((formId && id) || !id) && !data) {
        setLoading(!renderData)
        fetchData({
          inBackground: false
        })
      }
    }, [id, formId])

    useEffect(() => {
      if (selectedFormType) {
        setPdfDisplay(selectedFormType.includes('pdf'))
        setFormViewType(selectedFormType)
        setReadOnly(selectedFormType === 'printable')
      }
    }, [selectedFormType])

    useEffect(() => {
      if (
        renderData?.supportedFormType &&
        !renderData?.supportedFormType?.includes(
          selectedFormType || defaultFormType
        )
      ) {
        enqueueSnackbar(<Trans>FORM_WRONG_DEFAULT_FORMTYPE_ALERT</Trans>, {
          variant: 'error'
        })
      }
    }, [])

    useEffect(() => {
      if (id && Object.keys(connectedMap).length > 0 && !isPreview) {
        const objArray = id.split(';')
        objArray.forEach((string, index) => {
          const ident = string.split('=')[0]
          const obj = connectedMap[ident]
          const connectedObject = obj.sfObject
          if (
            connectedObject &&
            connectedObject.attributes.type === 'Account'
          ) {
            objArray[index] = ident + '=' + organization.id
          }
        })
        if (objArray.join(';') !== id) {
          setLoading(true)
          setId(objArray.join(';'))
          fetchData({
            reloadId: objArray.join(';'),
            inBackground: false
          })
        }
      }
    }, [organization.id])

    useEffect(() => {
      // This fixes issues with bold font not being used in first render of pdf
      Font.load({ fontFamily: 'Roboto' })
      Font.load({ fontFamily: 'Roboto', fontStyle: 'italic' })
      Font.load({ fontFamily: 'Roboto', fontWeight: 700 })
      Font.load({ fontFamily: 'Roboto', fontWeight: 700, fontStyle: 'italic' })
      Font.load({ fontFamily: 'Pigiarniq' })
      Font.load({ fontFamily: 'Pigiarniq', fontWeight: 700 })
      Font.load({ fontFamily: 'Pigiarniq', fontStyle: 'italic' })
      Font.load({
        fontFamily: 'Pigiarniq',
        fontWeight: 700,
        fontStyle: 'italic'
      })
    }, [])

    useEffect(() => {
      if (disableAutoTabTitle) return

      document.title = formTitlePdf
      // remember that this component last set the title
      ownTitleRef.current = formTitlePdf

      return () => {
        // on unmount or title change, if title is still ours, reset to default
        if (document.title === ownTitleRef.current) {
          document.title = defaultDocTitle
        }
      }
    }, [formTitlePdf, disableAutoTabTitle])

    const location = useLocation()
    const pathname = location?.pathname

    useEffect(() => {
      // This checks if first section is disabled and sets the initial selected section to first non-disabled section
      if (initialValues) {
        const initialDisabledIds = getDisabledIds({
          sections,
          elementsMap,
          values: initialValues,
          langVersion,
          connectedMap,
          describeMap,
          errors: {},
          pathname
        })
        if (currentStep === 0) {
          let valid = currentStep
          while (initialDisabledIds.includes(sectionConditionId + valid)) {
            valid++
            if (valid >= sections.length) {
              valid = 0
              break
            }
          }
          if (valid > 0) {
            setStep(valid)
          }
        }
      }
    }, [initialValues])

    useEffect(() => {
      // Update SF fields language info on user language change
      if (data) {
        connectedObjectQuery(data, {
          id,
          returnOnlyDescribe: true,
          langVersion,
          enqueueSnackbar,
          handleObjectMissing
        }).then(({ describeMap }) => {
          const newMap = { ...connectedMap }
          Object.values(newMap).forEach((obj) => {
            const { objectType } = obj
            const sfData = describeMap[objectType]
            obj.fieldsMap = sfData.fieldsMap
          })
          setConnectedMap(newMap)
          setDescribeMap(describeMap)
          setValidationSchema(
            constructValidationSchema({
              data,
              describeMap,
              langVersion,
              formikRef
            })
          )
        })
      }
    }, [user.language])

    useEffect(() => {
      if (formikRef.current) {
        formikRef.current.validateForm()
      }
    }, [validationSchema])

    // navigates to the first error on load if focusFirstError is true
    useEffect(() => {
      if (formikRef.current && focusFirstError && !hasNavigatedToFirstError.current) {
        const { errors, values } = formikRef.current ?? {}

        const disabledIds = getDisabledIds({
          sections,
          elementsMap,
          values,
          langVersion,
          connectedMap,
          describeMap,
          pdfView: returnPdf || pdfDisplay,
          errors,
          pathname
        })

        const parsedErrors = errorsToRender({
          errors,
          objectsFieldsMap,
          describeMap,
          disabledIds,
          elementsMap,
          langVersion,
          values
        })

        if (parsedErrors?.length > 0) {
          const firstError = parsedErrors[0]
          navigateToError({ section: firstError.sectionIndex, id: firstError.id })
          hasNavigatedToFirstError.current = true
        }
      }
    }, [formikRef.current?.errors, formikRef.current?.values, focusFirstError])

    const noStepper = sections.length < 2 || pdfDisplay

    const formHeaderContainerRef = useRef(null)
    const [isFormHeaderContainerSticky, setIsFormHeaderContainerSticky] = useState(false)

    const addMethodToValidationSchema = (method, id) => {
      const yupObj = constructValidationSchema({
        data,
        describeMap,
        langVersion: user.language?.split('_')[0] || 'en',
        returnRaw: true,
        formikRef
      })
      if (!yupObj[id]) {
        yupObj[id] = method
      } else {
        yupObj[id].concat(method)
      }
      setValidationSchema(Yup.object().shape(yupObj))
    }

    const { refetch } = useQuery({
      queryKey: ['connectedMapLastModifiedDates', id],
      queryFn: () => connectedObjectQuery(data, {
        enqueueSnackbar,
        langVersion,
        id,
        handleObjectMissing
      }),
      enabled: Boolean(
        id &&
        data?.displayesSavedInMeantimeWarning &&
        !isPreview &&
        !useMultiuser &&
        !loading
      ),
      refetchInterval: 60000,
      refetchOnWindowFocus: false,
      onSuccess: (queryResult) => {
        const toSet = { ...connectedMap }
        Object.keys(toSet).forEach((key) => {
          const setObj = toSet[key].sfObject
          const nowObj = queryResult.connectedMap[key]?.sfObject
          if (nowObj && setObj && setObj.LastModifiedDate) {
            setObj.LastModifiedDate = nowObj.LastModifiedDate
          }
        })
        setConnectedMap(toSet)
      }
    })

    const returnInDialog = ({
      component,
      loading,
      disableSave,
      pdfDocument
    }) => {
      if (loading) {
        return (
          <Dialog open maxWidth='lg' fullWidth>
            <DialogTitle>
              <Grid
                container
                wrap='nowrap'
                alignItems='flex-end'
                justifyContent='flex-end'
              >
                <Grid item>
                  <IconButton
                    onClick={() => {
                      onDialogClose()
                    }}
                    size='large'
                  >
                    <Icon>close</Icon>
                  </IconButton>
                </Grid>
              </Grid>
            </DialogTitle>
            <DialogContent style={{ width: '500px', minHeight: '60vh' }}>
              <Loading />
            </DialogContent>
          </Dialog>
        )
      }
      const formTitle = parseFormLabelText({
        text: data?.title[langVersion]?.text,
        langVersion,
        objectsFieldsMap,
        describeMap,
        returnString: true
      })
      return (
        <Dialog
          open
          maxWidth='lg'
          fullWidth
          scroll='paper'
          aria-labelledby='scroll-dialog-title'
          aria-describedby='scroll-dialog-description'
        >
          <DialogTitleWithIconClose handleClose={onDialogClose} label={formTitle} />
          <DialogContent style={{ minHeight: '60vh' }}>
            {component}
          </DialogContent>
        </Dialog>
      )
    }

    if (initialValuesError) {
      return (
        <FormErrorPopUp
          open
          error={initialValuesError}
          connectedMap={connectedMap}
          mainConnectedObject={mainObject}
          type='Initial values'
        />
      )
    }

    if (loading || !initialValues) {
      if (hideLoading) {
        return null
      }
      if (inDialog) {
        return returnInDialog({
          loading: true,
          disableSave: true
        })
      }
      return <Loading atCenter />
    }

    if (wrongAccountRole === 'prevent') {
      return null
    }

    const toReturn = (
      <ErrorBoundary
        onError={error => {
          queryCreateObject({
            type: 'Error Log',
            createData: {
              severity: 'error',
              description: sfOauthConfig.oauth2.redirectUri + history.location.pathname,
              longDescription: JSON.stringify({
                cause: error?.cause,
                name: error?.name,
                message: error?.message,
                stack: error?.stack
              })
            }
          })
        }}
        fallbackRender={({ error, resetErrorBoundary }) => {
          return (
            <FormDisplayContainer
              styleJSON={data?.style}
              style={style}
              viewType={containerViewType}
            >
              <FormErrorPopUp
                open
                error={{
                  message: JSON.stringify({
                    cause: error?.cause,
                    name: error?.name,
                    message: error?.message,
                    stack: error?.stack
                  })
                }}
                connectedMap={connectedMap}
                mainConnectedObject={mainObject}
                type='Unexpected error'
              />
            </FormDisplayContainer>
          )
        }}
      >
        <Formik
          innerRef={formikRef}
          validateOnBlur={false}
          validateOnChange
          validationSchema={!forceDisabled && validationSchema}
          validateOnMount
          enableReinitialize
          initialValues={initialValues}
          initialTouched={getInitialTouched(data)}
        >
          {({
            values,
            initialValues,
            isValidating,
            validateForm,
            setValues,
            setFieldValue,
            setFieldTouched,
            errors,
            dirty,
            ...fProps
          }) => {
            const noSaveButton =
              readOnly || pdfDisplay || forceDisabled || disabled || noDefaultSave || noConnectedObjects

            console.log('form values', values, connectedMap, formId)

            const saveDisabled =
              disabled ||
              (isPreview &&
                !sfOauthConfig.isTesting &&
                !hasRole(user.role, authRoles.tester)) ||
              !dirty

            const disabledIds = getDisabledIds({
              sections,
              elementsMap,
              values,
              langVersion,
              connectedMap,
              describeMap,
              pdfView: returnPdf || pdfDisplay,
              errors,
              pathname
            })

            const generatePDF = (props) => {
              const shouldCalculateFixedElementsInHook =
                props?.shouldCalculateFixedElementsInHook ?? true

              let pdfType = formViewType
              if (pdfType !== 'pdf' && pdfType !== 'fillable-pdf') {
                pdfType = 'pdf'
              }

              return (
                <FormPdfDocument
                  sections={sections}
                  title={formTitlePdf}
                  data={data}
                  describeMap={describeMap}
                  objectsFieldsMap={objectsFieldsMap}
                  connectedMap={connectedMap}
                  langVersion={langVersion}
                  formViewType={pdfType}
                  values={values}
                  elementsMap={elementsMap}
                  formikRef={formikRef}
                  disabledIds={disabledIds}
                  shouldCalculateFixedElementsInHook={shouldCalculateFixedElementsInHook}
                />
              )
            }

            return (
              <FormSavingHandler
                fetchData={fetchData}
                loading={loading}
                autosave={data?.autosave}
                readOnly={readOnly}
                displayUnsavedWarning={data?.displayUnsavedWarning}
                displaySaveFailedDialog={data?.displaySaveFailedDialog}
                formikRef={formikRef}
                isPreview={isPreview}
                disabled={disabled}
                // useMultiuser={useMultiuser}
                formId={formId}
                sections={sections}
                elementsMap={elementsMap}
                describeMap={describeMap}
                connectedMap={connectedMap}
                network={network}
                stage={stage}
                id={id}
                mainObject={mainObject}
                generatePDF={generatePDF}
                handleObjectMissing={handleObjectMissing}
                saveCallback={saveCallback}
                submitCallback={submitCallback}
                configuration={configuration}
                data={data}
                noSaveButton={data.noSaveButton || noSaveButton}
                saveDisabled={data.saveDisabled || saveDisabled}
                currentStep={currentStep}
                confirmationDialog={props.confirmationDialog}
                formTitlePdf={formTitlePdf}
                saveConnectedMap={props.saveConnectedMap}
                baseToSave={props.baseToSave}
              >
                {({ handleSave, handleSubmit, saving, setSaving, trySaving }) => {
                  let formTitle = parseFormLabelText({
                    text: data?.title?.[langVersion].text,
                    langVersion,
                    objectsFieldsMap,
                    describeMap,
                    returnString: true,
                    renderProps: {
                      connectedMap
                    }
                  })

                  if (formTitle.trim() === ':' && data?.title?.[langVersion].text) {
                    formTitle = data.title[langVersion].text
                  }

                  if (notSupportedError) {
                    return (
                      <Alert severity='error'>
                        <AlertTitle>
                          <Trans>FORM_USED_AND_SUPPORTED_FORM_TYPES_MISSMATCH</Trans>
                        </AlertTitle>
                      </Alert>
                    )
                  }

                  let pdfDocument
                  // check if there is a submit button among the elements.
                  // If there is and there is also at least one element with pdf view support, it means that we need a pdfDocument to be prepared and passed to the submit button
                  const hasSupportedFormType = supportedFormViews.includes('pdf') || supportedFormViews.includes('fillable-pdf')

                  if (onPdfCreated) {
                    const pdfDocument = generatePDF({
                      shouldCalculateFixedElementsInHook: false
                    })
                    onPdfCreated(pdfDocument, formTitlePdf)
                  }

                  if (hasSupportedFormType && (returnPdf || pdfDisplay || showPrintButton)) {
                    pdfDocument = generatePDF()

                    if (returnPdf) {
                      return returnPdf(pdfDocument, formTitlePdf, fetchData)
                    }

                    if (pdfDisplay) {
                      return (
                        <FormDisplayContainer
                          styleJSON={data?.style}
                          style={{
                            ...style,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            minHeight: returnInDialog && '80vh'
                          }}
                          viewType={containerViewType}
                        >
                          {!returnInDialog && (
                            <Typography variant='h4' style={{ textAlign: 'center' }}>
                              {formTitle}
                            </Typography>
                          )}
                          <PDFViewer showToolbar={false} style={{ flexGrow: 1 }}>
                            {pdfDocument}
                          </PDFViewer>
                          <PDFDownloadLink
                            fileName={formTitle + '.pdf'}
                            document={pdfDocument}
                          >
                            <Grid
                              container
                              justifyContent='center'
                              style={{ paddingTop: '20px' }}
                            >
                              <Button
                                color='primary'
                                variant='contained'
                                disabled={loading}
                                fullWidth
                              >
                                <Trans>Download</Trans>
                                <Icon style={{ marginLeft: 5 }}>download</Icon>
                              </Button>
                            </Grid>
                          </PDFDownloadLink>
                        </FormDisplayContainer>
                      )
                    }
                  }

                  if (!isValidating && !forceDisabled && validationSchema) {
                    checkIfFormValidationShouldRebuild({ sections, values })
                  }

                  const handleNext = () => {
                    let toSet = currentStep + 1
                    while (disabledIds.includes(sectionConditionId + toSet)) {
                      toSet++
                      if (toSet >= sections.length) {
                        toSet = currentStep
                        break
                      }
                    }
                    scrollToTop()
                    setStep(toSet)
                  }

                  const handleBack = () => {
                    let toSet = currentStep - 1

                    while (disabledIds.includes(sectionConditionId + toSet)) {
                      toSet--
                      if (toSet < 0) {
                        toSet = currentStep
                        break
                      }
                    }
                    scrollToTop()
                    setStep(toSet)
                  }

                  const parsedErrors = errorsToRender({
                    errors,
                    objectsFieldsMap,
                    describeMap,
                    disabledIds,
                    elementsMap,
                    langVersion,
                    values
                  })

                  const firstError = parsedErrors[0]

                  const handleGoToFirstError = () => {
                    setStep(firstError.sectionIndex)
                    if (scrollbarContentRef && scrollbarContentRef.current) {
                      setTimeout(() => {
                        const domElement = document.getElementById(firstError.id)
                        scrollbarContentRef.current.scrollTop(domElement.offsetTop)
                      }, 300)
                    } else {
                      setTimeout(() => {
                        const domElement = document.getElementById(firstError.id)
                        domElement.scrollIntoView({ behavior: 'instant', block: 'start' })
                      }, 300)
                    }
                  }

                  const renderTitle = !inDialog && !['horizontal', 'vertical'].includes(collapsable) // && !isFormHeaderContainerSticky
                  const noHeaderTitle = !formTitle || Boolean(hideFormTitle)
                  const noHeaderButtons = noSaveButton && !showPrintButton && (!props.extraButtons || props.extraButtons.length === 0)
                  const hideHeaderContainer = noHeaderTitle && noHeaderButtons

                  const formContent = (
                    <FormContextProvider
                      network={network}
                      generatePDF={generatePDF}
                      formTitle={formTitlePdf}
                      sectionsNumber={sections.length}
                      currentStep={currentStep}
                      actionHandlers={props.actionHandlers}
                      activePrequalificationsMap={props.activePrequalificationsMap}
                      mainConnectedObject={mainObject}
                      formViewType={formViewType}
                    >
                      {formId && (
                        <FormMissingObjectsObserver
                          formId={formId}
                          missingObjects={missingObjects}
                        />)}
                      <FormDisplayContainer
                        styleJSON={data?.style}
                        style={{
                          ...style,
                          ...(hideHeaderContainer ? { paddingTop: 0 } : {})
                        }}
                        viewType={containerViewType}
                      >
                        <div ref={mouseDetectRef}>
                          {renderTitle && !hideHeaderContainer && (
                            customTitleRender
                              ? customTitleRender(hideFormTitle ? '' : formTitle)
                              : (
                                <Typography
                                  className='form-header-title'
                                  sx={{
                                    color: props?.formTitlePrimaryColor
                                      ? 'primary.main'
                                      : '',
                                    textAlign: props?.centerFormTitle
                                      ? 'center'
                                      : 'left'
                                  }}
                                >
                                  {!hideFormTitle ? formTitle : ''}
                                </Typography>
                                )
                          )}
                          {(!hideHeaderContainer || !noStepper) && (
                            <div
                              className={`form-header-container-${containerViewType === 'clean'
                                ? doNotUseStickyHeader ? 'clean-static' : 'clean'
                                : doNotUseStickyHeader ? 'color-static' : 'color'}`}
                              ref={formHeaderContainerRef}
                            >
                              {!noStepper
                                ? (
                                  <FormStepper
                                    showStepperValidation={props.showStepperValidation}
                                    stickyComponentRef={formHeaderContainerRef}
                                    isStickyCallback={setIsFormHeaderContainerSticky}
                                    renderDropdown={isFormHeaderContainerSticky}
                                    formType={formViewType}
                                    disabled={disabled || readOnly}
                                    disabledIds={disabledIds}
                                    setStep={setStep}
                                    currentStep={currentStep}
                                    parsedErrors={parsedErrors}
                                    sections={sections}
                                    handleStepChange={(index) => {
                                      setStep(index)
                                      scrollToTop()
                                    }}
                                    downloadButton={showPrintButton}
                                    saveButton={!noSaveButton}
                                    renderStepLabel={(index, section) => {
                                      if (section.title?.portalTranslationId) {
                                        return getTranslation({
                                          portalTranslationId: section.title.portalTranslationId,
                                          langVersion: language
                                        })
                                      }
                                      return parseFormLabelText({
                                        text: section.title,
                                        langVersion,
                                        objectsFieldsMap,
                                        describeMap,
                                        renderProps: {
                                          connectedMap
                                        }
                                      })
                                    }}
                                    extraButtons={props.extraButtons}
                                    smallSpace={smallSpace}
                                    setSmallSpace={setSmallSpace}
                                    ButtonsElement={
                                      <StepperButtons
                                        formTitle={formTitlePdf}
                                        hideSaveButton={noSaveButton}
                                        pdfDocument={showPrintButton && supportedFormViews.includes('pdf') && pdfDocument}
                                        noStepper={noStepper}
                                        saving={saving}
                                        disableSave={saveDisabled}
                                        elementsMap={elementsMap}
                                        handleSave={() => trySaving({ values })}
                                        handleNext={handleNext}
                                        handleBack={handleBack}
                                        steps={sections.filter(
                                          (section, index) =>
                                            !disabledIds.includes(sectionConditionId + index)
                                        )}
                                        activeStep={getValidCurrentStepIndex({
                                          realIndex: currentStep,
                                          disabledIds
                                        })}
                                        generatePDF={generatePDF}
                                        showPDFButton={showPrintButton}
                                        disablePDFDownload={blockPDFDownload}
                                        showNavigation={false}
                                        renderShrink={smallSpace.buttons && smallSpace.stepper}
                                        renderDropdown={smallSpace.stepper || isFormHeaderContainerSticky}
                                        extraButtons={props.extraButtons}
                                        firstError={firstError}
                                        handleGoToFirstError={handleGoToFirstError}
                                        disabled={disabled}
                                      />
                            }
                                  />
                                  )
                                : (
                                  <Box sx={{ mb: '7px', mt: '16px' }}>
                                    <StepperButtons
                                      formTitle={formTitlePdf}
                                      hideSaveButton={noSaveButton}
                                      pdfDocument={showPrintButton && supportedFormViews.includes('pdf') && pdfDocument}
                                      noStepper={noStepper}
                                      saving={saving}
                                      disableSave={saveDisabled}
                                      elementsMap={elementsMap}
                                      handleSave={() => trySaving({ values })}
                                      generatePDF={generatePDF}
                                      showPDFButton={showPrintButton}
                                      disablePDFDownload={blockPDFDownload}
                                      showNavigation={false}
                                      renderShrink={smallSpace.buttons && smallSpace.stepper}
                                      extraButtons={props.extraButtons}
                                      firstError={firstError}
                                      handleGoToFirstError={handleGoToFirstError}
                                      disabled={disabled}
                                    />
                                  </Box>)}
                              <Divider />
                            </div>
                          )}

                          {formViewType === 'editable' &&
                            <Typography className='required-fields-label'>
                              <Trans>
                                REQUIRED_FIELDS_PROMPRT_EDITABLE_FORM
                              </Trans>
                            </Typography>}

                          <CollapseElement
                            orientation={collapsable}
                            title={hideFormTitle ? '' : formTitle}
                          >
                            {!data?.translatedFor?.[langVersion_]?.value && (
                              <Alert severity='info' style={{ marginBottom: 10 }}>
                                <Trans>NOT_SUPPORTED_FORM_LANGUAGE</Trans>
                              </Alert>
                            )}
                            {infoBanner}
                            {banner &&
                              <Alert severity={banner.type || 'info'}>
                                <AlertTitle>
                                  {banner.title}
                                </AlertTitle>
                              </Alert>}
                            {sections[currentStep]?.elements?.map((item, index) => {
                              // Disable elements if the section state is "disable" and the section condition is met
                              const currentSection = sections[currentStep]
                              const currentSectionCondition =
                          currentSection?.conditions?.[0]
                              const checkCurrentSectionCondition =
                          currentSectionCondition
                            ? isConditionMet({
                              condition: currentSectionCondition,
                              elementsMap,
                              values,
                              langVersion,
                              describeMap,
                              connectedMap,
                              errors
                            })
                            : true
                              const sectionDisabled =
                          checkCurrentSectionCondition.conditionMet &&
                          checkCurrentSectionCondition?.state === 'disable'

                              return (
                                <div key={index}>
                                  <FormElementGroup
                                    item={{
                                      ...item,
                                      value: values[item.id]
                                    }}
                                    lastInSection={sections[currentStep].elements.length === index + 1}
                                    formViewType={formViewType}
                                    values={values}
                                    disabledIds={disabledIds}
                                    baseErrors={errors}
                                    errors={parsedErrors}
                                    objectsFieldsMap={objectsFieldsMap}
                                    connectedMap={connectedMap}
                                    elementsMap={elementsMap}
                                    describeMap={describeMap}
                                    formikRef={formikRef}
                                    langVersion={langVersion}
                                    key={index}
                                    navigateToError={navigateToError}
                                    useMultiuser={useMultiuser}
                                    muBag={{
                                      formId: formRealmId(organizationId, id),
                                      token: multiuserSessionToken,
                                      userId: user.userId
                                    }}
                                    sectionIndex={currentStep}
                                    preview={isPreview}
                                    saveButtonClicked={trySaving}
                                    handleSubmit={handleSubmit}
                                    saving={saving}
                                    configuration={configuration}
                                    disabled={Boolean(
                                      disabled ||
                                  saving ||
                                  sectionDisabled || forceDisabled
                                    )}
                                    saveDisabled={Boolean(disabled || isPreview)}
                                    renderPrint={readOnly}
                                    pdfDocument={pdfDocument}
                                  />
                                </div>
                              )
                            })}
                            {children && children({
                              values,
                              saveDisabled,
                              submitDisabled: parsedErrors.length > 0 || saveDisabled,
                              handleSave,
                              validateForm,
                              setFieldTouched
                            })}
                            <div style={{ paddingTop: 15, paddingRight: 15 }}>
                              {Boolean(!noStepper) &&
                                <StepperButtons
                                  formTitle={formTitlePdf}
                                  hideSaveButton={noSaveButton}
                                  noStepper={noStepper}
                                  saving={saving}
                                  disableSave={saveDisabled}
                                  elementsMap={elementsMap}
                                  handleSave={() => trySaving({ values })}
                                  handleNext={handleNext}
                                  handleBack={handleBack}
                                  steps={sections.filter(
                                    (section, index) =>
                                      !disabledIds.includes(sectionConditionId + index)
                                  )}
                                  activeStep={getValidCurrentStepIndex({
                                    realIndex: currentStep,
                                    disabledIds
                                  })}
                                  firstError={firstError}
                                  handleGoToFirstError={handleGoToFirstError}
                                  disabled={disabled}
                                />}
                            </div>
                          </CollapseElement>

                        </div>

                      </FormDisplayContainer>
                    </FormContextProvider>
                  )

                  if (useMultiuser) {
                    const { objectsFieldsMap } = getObjectsFieldsMap({
                      data,
                      connectedMap,
                      describeMap
                    })
                    return (
                      <FormMultiuser
                        elementsMap={elementsMap}
                        realmId={formRealmId(organizationId, id)}
                        mousePosition={mousePosition}
                        multiuserSessionToken={multiuserSessionToken}
                        setMultiuserSessionToken={setMultiuserSessionToken}
                        currentStep={currentStep}
                        formikRef={formikRef}
                        setSaving={setSaving}
                        handleSave={handleSave}
                        fetchData={fetchData}
                        programManagerSession={
                    params.organizationId && checkAuth(authRoles.pm, user.role)
                  }
                        initialValues={initialValues}
                        formMetadata={{
                          sessionName: parseFormLabelText({
                            returnString: true,
                            text: data?.title[langVersion]?.text,
                            objectsFieldsMap,
                            describeMap
                          }),
                          organizationName: organization.organisationsName,
                          organizationId,
                          formId,
                          url: id
                        }}
                        handleMultiuserLoadSuccess={({ initialValues }) => {
                          setInitialValues(initialValues)
                          setLoading(false)
                        }}
                      >
                        {formContent}
                      </FormMultiuser>
                    )
                  } else {
                    return formContent
                  }
                }}
              </FormSavingHandler>
            )
          }}
        </Formik>
      </ErrorBoundary>
    )

    const content = inDialog
      ? returnInDialog({
        component: toReturn
      })
      : toReturn

    return (
      <>
        {showLangWarning && (
          <PopUpDialogue
            open={showLangWarning}
            dialogTitle={<Trans>FORM_LANGUAGE_RESTRICTION_WARNING_TITLE</Trans>}
            dialogMessege={<Trans>FORM_LANGUAGE_RESTRICTION_WARNING</Trans>}
            okText={<Trans>FORM_LANGUAGE_RESTRICTION_CONFIRM</Trans>}
            confirmFunction={switchToSupportedLanguage}
            closeFunction={switchToSupportedLanguage}
          />
        )}
        {content}
      </>
    )
  }
)

export const extractFormFieldDetails = ({
  connectedField,
  connectedMap,
  describeMap,
  connectedObject
}) => {
  const { subObject, name } = connectedField
  if (!subObject) {
    return connectedMap?.[connectedObject]?.fieldsMap?.[name] || {}
  } else {
    const subFieldName = name.split('.')[1]
    const subObjectName = name.split('.')[0]
    if (!subFieldName || !subObjectName) {
      console.error('Could not find field details for: ', name)
      return null
    }
    const fields = describeMap[subObjectName]?.fields
    let details
    fields.some((field) => {
      if (field.name === subFieldName) {
        details = field
      }
      return field.name === subFieldName
    })
    return details
  }
}

export default Form
